import { type Ref } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import clsx from 'clsx';

import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';

import { type WidgetAction } from './Widget';

import buttonStyles from '../buttons/button.module.scss';
import styles from './widget.module.scss';

type IconControlProps = {
  action: WidgetAction;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
};

type IconButtonProps = IconControlProps & {
  loading?: boolean;
  onClick?: () => void | Promise<void>;
  ref?: Ref<HTMLButtonElement | null>;
};

type IconLinkProps = IconControlProps & {
  to: LinkProps['to'];
  ref?: Ref<HTMLAnchorElement | null>;
};

function ActionIcon({ action }: { action: WidgetAction }) {
  return action.icon.svgIcon ? (
    <SVGIcon name={action.icon.name} key={action.icon.name} />
  ) : (
    <GoogleFontIcon
      key={action.icon.name}
      className={action.icon.className}
      name={action.icon.name}
      solid={action.icon.solid ?? false}
      small
    />
  );
}

export function IconButton({
  action,
  loading,
  onClick,
  className,
  ref,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: IconButtonProps) {
  const hasLabel = Boolean(action.label);

  return (
    <Button
      ref={ref}
      loading={loading}
      disabled={action.disabled}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      icon={<ActionIcon action={action} />}
      onClick={onClick}
      title={action.title}
      variant={hasLabel ? 'primary' : 'text'}
      className={clsx(!hasLabel && styles['header__action'], className)}
    >
      {action.label}
    </Button>
  );
}

export function IconLink({
  action,
  to,
  className,
  ref,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  ...rest
}: IconLinkProps) {
  const hasLabel = Boolean(action.label);

  return (
    <Link
      {...rest}
      ref={ref}
      to={to}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      title={action.title || undefined}
      className={clsx(
        buttonStyles['root'],
        buttonStyles['text'],
        !hasLabel && buttonStyles['root--iconOnly'],
        !hasLabel && styles['header__action'],
        className
      )}
    >
      <span
        className={clsx(
          buttonStyles['inner'],
          hasLabel && buttonStyles['inner--iconAndText']
        )}
      >
        <ActionIcon action={action} />
        {action.label}
      </span>
    </Link>
  );
}
